import { useContext } from "react";
import { io } from "socket.io-client";
import { BASE_API, useHttp } from "../hooks/http";
import { SessionContext } from "../provider/SessionProvider";
import { IPaginatedResponse, PaginatedResponse } from "../models/ApiResponse";
import { TherapistChatContext } from "../provider/TherapistChatProvider";
import { IMessage } from "react-native-gifted-chat";

export interface ChatMessage {
    therapist_id: number;
    user_id: number;
    message: string;
    from_therapist: boolean;
    send_at: Date;

    //This property is not in the API,
    // is used to show a flag of sent in the chat
    _ok: boolean;
}

export const useChat = () => {

    const http = useHttp(BASE_API, true);

    const chatContext = useContext(TherapistChatContext);
    const session = useContext(SessionContext);

    const listHistory = async (pageNum: number, search?: string): Promise<PaginatedResponse<ChatMessage>> => {
        const response = await http.get<IPaginatedResponse<ChatMessage>>(`/patientapi/chat/history`, {
            page_num: pageNum.toString(),
            page_size: "30",
            search
        })
            .then((response) => response?.data || { items: [], total: 0 });

        response.items.forEach((message) => {
            message.send_at = new Date(message.send_at);
            message._ok = true;
        });

        return new PaginatedResponse(response, pageNum, 20);
    };

    const connectChatSocket = async (): Promise<void> => {
        if (chatContext.socket?.value && chatContext.socket?.value?.connected) {
            return;
        }

        const socket = io(BASE_API + `/patientapi/chat`, {
            auth: {
                token: session?.session?.current?.accessToken
            }
        });

        return await new Promise((resolve, reject) => {
            socket.on('connect', () => {
                chatContext.socket?.set(socket);
                resolve();
            });

            socket.on('error', () => {
                reject();
            });

            socket.on('message', (message: ChatMessage) => {
                console.log('Message received in socket:', message);
                message.send_at = new Date(message.send_at);
                chatContext.eventListener?.value?.dispatchEvent(new CustomEvent('message', { detail: message }));
            });
        });
    };

    const sendMessage = async (message: IMessage): Promise<ChatMessage> => {
        if (!chatContext.socket?.value) {
            throw new Error('Chat socket is not connected');
        }

        return new Promise((resolve) => {
            chatContext.syncingMessages?.set((prev) => {
                prev[message._id] = message;
                return prev;
            });

            let pen = chatContext.socket?.value?.emit('message', { message: message.text }, (msg: ChatMessage) => {
                msg.send_at = new Date(msg.send_at);
                msg._ok = true;

                chatContext.syncingMessages?.set((prev) => {
                    delete prev[message._id];
                    return prev;
                });

                resolve(msg)
            })
        });
    };

    const needsSync = (message: IMessage) => {
        return chatContext.syncingMessages?.value[message._id] === undefined;
    };

    return {
        listHistory,
        connectChatSocket,
        sendMessage,
        needsSync,
    };
};
